import { apiClient } from './client';

/**
 * The Hardware Request device / accessory catalogue.
 *
 * ⛔ Moved here from manage on 30/09/2026 (Stage 1). The tables live in
 * `oscar.*`; the request form in manage reads them through wrapper views and
 * cannot write them. This is the only editor.
 *
 * Reads are open to any authenticated Oscar user; every save is `oscar-admin`,
 * enforced server-side.
 */

export interface HwCatalogueDevice {
  DeviceOptionId: number;
  Name: string;
  SortOrder: number;
  /** "No Device Required, Accessory Request Only" — a device row, by design. */
  IsAccessoryOnly: boolean;
  /** iPhone/iPad take OneBlink's conditional accessory path; everything else does not. */
  AsksAccessoryCondition: boolean;
  /** IT can remote-lock it (iPhone AND iPad). */
  CanBeLocked: boolean;
  /** It has a number to divert (iPhone ONLY). An iPad has none. */
  HasMobileNumber: boolean;
  /** Set by migration 057; not editable here. */
  RequiresFleetNumber: boolean;
  /** ⚠ Ticking this on the FIRST device turns the branch option on for everybody. */
  IsBranchUse: boolean;
  IsActive: boolean;
  /** On a submission already — so its name is frozen. */
  IsUsed: boolean;

  /**
   * How many to hold ACROSS EVERY WAREHOUSE (080).
   *
   * ⛔ Not per stock line: `StockItem` carries one `LocationId`, so the same
   * description at two warehouses is two rows, and a per-line threshold would
   * mean "twenty at Sydney AND twenty at Brisbane".
   *
   * ⚠ Null means do not watch it. Not zero — zero means "tell me when we have
   * none", which is a real and different instruction.
   */
  MinQty: number | null;
  /** Summed across every mapped stock line, at every warehouse. */
  TotalOnHand: number;
  /** ⚠ Zero with a threshold set means nothing is mapped to it yet. */
  StockLines: number;
  IsLow: boolean;

  /**
   * What one of these is reckoned to cost, ex GST (083).
   *
   * ⛔ INDICATIVE, NOT THE PRICE PAID. It prefills the amount when a request
   * is fulfilled by purchase, and the fulfiller can change it. What reaches
   * `project.ProjectCost` is whatever was in the box, so a stale catalogue
   * price cannot post itself.
   *
   * ⚠ Null means no price recorded and the box starts empty. Not zero, which
   * would prefill a free laptop.
   */
  UnitCost: number | null;

  /**
   * What makes the variants of a thing siblings (090) — "Laptop" over
   * Standard, High Spec, OLT and Tough Book.
   *
   * ⛔ Null is UNGROUPED, and ungrouped behaves exactly as a device did before
   * 090: restricted if it has an allocation, offered to everybody if it does
   * not. Nothing is forced into a group.
   */
  DeviceGroup: string | null;

  /**
   * Which kind of asset this device REPLACES — one of `oscar.Asset.Category`'s
   * own values (090).
   *
   * ⚠ NOT the same thing as the group. Monitors, headsets and webcams are
   * three separate groups (a role may hold one of each) that share the asset
   * category `Other`, because the asset register has no finer word for a
   * peripheral.
   */
  AssetCategory: string | null;

  /**
   * The variant a role falls back to when nothing in its group is allocated
   * to it (090).
   *
   * ⛔ THIS IS WHAT CLOSES THE HOLE: four laptop variants allocated separately
   * meant every one of them was restricted, and a role on none of the four
   * lists was offered no laptop at all.
   */
  IsGroupDefault: boolean;
}

export interface HwCatalogueAccessory {
  AccessoryOptionId: number;
  Name: string;
  SortOrder: number;
  AccessoryGroupId: number | null;
  IsActive: boolean;
  /** The ids the editor round-trips. */
  DeviceOptionIds: number[];
  /** The names the list shows. */
  ForDevices: string[];
  /**
   * The devices this component is REQUIRED for - a subset of DeviceOptionIds. Empty means optional
   * everywhere, which is how every one behaved before 30/10/2026. ⚠ RECORDED ONLY: the request form
   * does not read it yet (stock/catalogue consolidation, PR 3).
   */
  RequiredDeviceOptionIds: number[];
  IsUsed: boolean;

  /**
   * What one of these is reckoned to cost, ex GST (086).
   *
   * ⛔ INDICATIVE, exactly as on the device. ⚠ It does NOT prefill the
   * purchase amount — that comes from a single priced DEVICE, because a
   * request can ask for several accessories and nothing records how many of
   * each. Stored and shown; adding it up would be a guess that looks like
   * arithmetic.
   */
  UnitCost: number | null;
}

export interface HwCatalogueGroup {
  AccessoryGroupId: number;
  Name: string;
}

export interface HwCatalogue {
  Devices: HwCatalogueDevice[];
  Accessories: HwCatalogueAccessory[];
  Groups: HwCatalogueGroup[];
}

export interface HwDeviceInput {
  /** Null or absent creates a device; a number updates that one. */
  DeviceOptionId?: number | null;
  Name: string;
  SortOrder: number;
  IsAccessoryOnly: boolean;
  AsksAccessoryCondition: boolean;
  CanBeLocked: boolean;
  HasMobileNumber: boolean;
  IsBranchUse: boolean;
  IsActive: boolean;
  /** Company-wide reorder point; null switches the watch off (080). */
  MinQty: number | null;
  /** Indicative unit cost ex GST; null means no price recorded (083). */
  UnitCost: number | null;
  /** What makes the variants siblings (090). Null or blank means ungrouped. */
  DeviceGroup: string | null;
  /** Which `oscar.Asset.Category` this replaces (090). Null means nothing does. */
  AssetCategory: string | null;
  /** The fallback for a role with no entitlement in this group (090). */
  IsGroupDefault: boolean;
}

/**
 * What `oscar.Asset.Category` may hold — `CK_Asset_Category`, migration 004.
 *
 * ⛔ A value outside this set matches no asset, so a replacement request would
 * narrow to an empty list and the device would never be offered, with nothing
 * on screen to explain why. The server refuses one; this is what the picker
 * offers so it cannot be typed.
 */
export const ASSET_CATEGORIES = [
  'Laptop', 'Desktop', 'Tablet', 'Mobile', 'MFD', 'Server', 'Network', 'SIM', 'Other',
] as const;

export interface HwAccessoryInput {
  /** Null or absent creates an accessory; a number updates that one. */
  AccessoryOptionId?: number | null;
  Name: string;
  SortOrder: number;
  AccessoryGroupId: number | null;
  IsActive: boolean;
  DeviceOptionIds: number[];
  /** Each must also be in DeviceOptionIds; refused for a component in an "only one of" group. */
  RequiredDeviceOptionIds: number[];
  /** Indicative unit cost ex GST; null means no price recorded (086). */
  UnitCost: number | null;
}

export interface HwGroupInput {
  /** Null or absent creates a group; a number renames or retires that one. */
  AccessoryGroupId?: number | null;
  Name: string;
  /**
   * True retires it. Refused while accessories still reference it.
   *
   * ⚠ OPTIONAL, and omitting it means FALSE on the server — so a plain Save on
   * a retired group brings it back. That is the existing behaviour and the
   * reason the editor has a separate Retire button rather than a toggle.
   */
  IsDeleted?: boolean;
}

/** GET /api/hardware-catalogue — everything, including retired options. */
export const getHardwareCatalogue = (signal?: AbortSignal) =>
  apiClient.get<HwCatalogue>('/api/hardware-catalogue', signal);

/**
 * POST /api/hardware-catalogue/device — create or update one device (admin).
 *
 * ⚠ `deviceOptionId` is the row that was written, new or existing. The
 * catalogue screen uses it to hold stock for a device at the moment it is
 * created, so the name is typed once rather than again on the Stock page.
 */
export const saveHardwareDevice = (body: HwDeviceInput) =>
  apiClient.post<{ saved: boolean; deviceOptionId: number }>(
    '/api/hardware-catalogue/device', body);

/** POST /api/hardware-catalogue/accessory — create or update one accessory (admin). */
export const saveHardwareAccessory = (body: HwAccessoryInput) =>
  apiClient.post<{ saved: boolean }>('/api/hardware-catalogue/accessory', body);

/** POST /api/hardware-catalogue/group — create, rename or retire a group (admin). */
export const saveHardwareGroup = (body: HwGroupInput) =>
  apiClient.post<{ saved: boolean; accessoryGroupId: number }>(
    '/api/hardware-catalogue/group', body);
